import type { CSSProperties } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { keepPreviousData } from '@tanstack/react-query'

import { useGetAdminCalendar } from '../../../api/generated/endpoints/admin/admin'
import type { CalendarBooking } from '../../../api/generated/model'
import {
  KIND_LABELS,
  KIND_STYLES,
  buildDayCells,
  hourRows,
  SIN_NOMBRE,
} from '../../../lib/admin-calendar'
import { isLocalDate } from '../../../lib/slots'
import {
  addDays,
  formatDayLong,
  formatWeekday,
  dayOfMonth,
  todayAtFacility,
} from '../../../lib/time'

/**
 * One day at a time, two bay columns, quantised to the hour.
 *
 * NOT a seven-day grid. Seven days times two bays on an iPad leaves about 72px
 * per column, which is under the tap-target floor in one axis before a name is
 * even printed in it. The week is reachable through the day strip above, where
 * each day is a target you cannot miss.
 *
 * The 10-minute grid is deliberately absent here: 73 targets a day is exactly
 * what this layout exists to avoid. It appears one level in, inside the form,
 * already answered — so somebody saying "ten o'clock" never meets it.
 */
export const Route = createFileRoute('/_authed/_admin/admin/calendar')({
  validateSearch: (search: Record<string, unknown>): { date: string; bay?: number } => ({
    date: isLocalDate(search.date) ? search.date : todayAtFacility(),
    // Undefined means every bay, which is the default and the common case.
    bay: typeof search.bay === 'number' ? search.bay : undefined,
  }),
  component: AdminCalendarPage,
})

function AdminCalendarPage() {
  const { date, bay: selectedBay } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const today = todayAtFacility()

  // The strip always starts today: the coach thinks forward, never back.
  const weekStart = today
  const week = Array.from({ length: 7 }, (_, offset) => addDays(weekStart, offset))

  // But the FETCH has to cover whatever day is actually selected, which the
  // strip does not bound: "Día anterior" walks off the front of it, and so does
  // any link from another screen. Asking only for the strip's week meant every
  // day outside it rendered as a whole facility standing empty -- right
  // heading, every slot free, every booking that day invisible. Yesterday was
  // already outside it.
  const from = date < week[0] ? date : week[0]
  const to = date > week[6] ? date : week[6]

  const calendar = useGetAdminCalendar(
    { from, to },
    {
      query: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        // Keeps the grid on screen while another day loads, instead of
        // collapsing to a skeleton and throwing away his place.
        placeholderData: keepPreviousData,
      },
    },
  )

  if (calendar.isPending) return <p className="text-xl">Cargando el calendario…</p>
  if (calendar.isError) {
    return (
      <div role="alert" className="rounded-lg border-2 border-red-300 bg-red-50 p-6">
        <p className="text-xl text-red-900">No se pudo cargar el calendario.</p>
        <button
          type="button"
          onClick={() => void calendar.refetch()}
          className="mt-4 min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Reintentar
        </button>
      </div>
    )
  }

  const data = calendar.data
  const rules = {
    opensAt: data.opens_at,
    closesAt: data.closes_at,
    slotMinutes: data.slot_minutes,
    stepMinutes: data.step_minutes,
  }
  // Cells are built for EVERY bay and the filter only narrows what is drawn,
  // so switching chips never refetches and never recomputes.
  const cells = buildDayCells(
    data.bookings,
    date,
    data.bays.map((bay) => bay.id),
    rules,
  )

  // A chip for a bay that has gone out of service would leave the coach staring
  // at an empty column, so a stale `?bay=` falls back to showing all of them.
  const shown = data.bays.filter((bay) => selectedBay === undefined || bay.id === selectedBay)
  const bays = shown.length > 0 ? shown : data.bays

  // Tailwind cannot build a class name at runtime -- it scans the source -- and
  // the track count depends on how many simulators the facility has, which the
  // settings screen can now change. So the count travels as a custom property
  // and the arbitrary variant applies it AT md ONLY: a plain inline
  // `gridTemplateColumns` would also apply below md and break the stacking
  // that keeps tap targets full width on a phone.
  const columns = {
    '--cols': `5rem repeat(${bays.length}, minmax(0, 1fr))`,
  } as CSSProperties

  const countFor = (day: string) => data.bookings.filter((booking) => booking.date === day).length

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          to="/admin/calendar"
          search={(prev) => ({ ...prev, date: addDays(date, -1) })}
          className="inline-flex min-h-16 items-center rounded-md border-2 border-slate-400 bg-white px-6 text-lg focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          &lsaquo;&nbsp; Día anterior
        </Link>

        <h1 className="text-3xl font-semibold">
          {formatDayLong(date)}
          {date === today && <span className="ml-3 text-xl text-slate-700">Hoy</span>}
          {date === addDays(today, 1) && (
            <span className="ml-3 text-xl text-slate-700">Mañana</span>
          )}
        </h1>

        <Link
          to="/admin/calendar"
          search={(prev) => ({ ...prev, date: addDays(date, 1) })}
          className="inline-flex min-h-16 items-center rounded-md border-2 border-slate-400 bg-white px-6 text-lg focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Día siguiente &nbsp;&rsaquo;
        </Link>
      </div>

      {/* The week, as seven targets you cannot miss. All seven fit without
          horizontal scrolling -- unlike the customer DayStrip, which scrolls. */}
      <div className="grid grid-cols-4 gap-3 sm:grid-cols-7">
        {week.map((day) => {
          const selected = day === date
          const count = countFor(day)
          return (
            <Link
              key={day}
              to="/admin/calendar"
              search={(prev) => ({ ...prev, date: day })}
              aria-current={selected ? 'date' : undefined}
              className={[
                'flex min-h-22 flex-col items-center justify-center rounded-lg border-2 px-2 py-3',
                'focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                selected
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="text-sm font-medium uppercase">
                {day === today ? 'Hoy' : day === addDays(today, 1) ? 'Mañana' : formatWeekday(day)}
              </span>
              <span className="text-2xl font-semibold tabular-nums">{dayOfMonth(day)}</span>
              <span className={selected ? 'text-sm text-slate-300' : 'text-sm text-slate-700'}>
                {count === 0 ? 'libre' : `${count} reservada${count === 1 ? '' : 's'}`}
              </span>
            </Link>
          )
        })}
      </div>

      {data.bays.length > 1 && (
        <div className="flex flex-wrap gap-3">
          {/* All of them by default: the whole point of this screen is seeing
              the day at once. The chips are for when a third simulator makes
              three columns on an iPad stop being readable. */}
          {[{ id: undefined, name: 'Todos' }, ...data.bays].map((bay) => (
            <button
              key={bay.id ?? 'all'}
              type="button"
              onClick={() =>
                void navigate({ search: (prev) => ({ ...prev, bay: bay.id }), replace: true })
              }
              aria-pressed={selectedBay === bay.id}
              className={[
                'min-h-14 rounded-full border-2 px-6 text-lg',
                'focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                selectedBay === bay.id
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-400 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              {bay.name}
            </button>
          ))}
        </div>
      )}

      {/* Bay headings, then one row per hour. Below md the bays stack rather
          than shrink -- targets never get smaller to fit. */}
      <div className="space-y-3">
        <div
          className="hidden gap-3 md:grid md:[grid-template-columns:var(--cols)]"
          style={columns}
        >
          <div />
          {bays.map((bay) => (
            <h2
              key={bay.id}
              className="rounded-md bg-slate-200 py-3 text-center text-xl font-semibold"
            >
              {bay.name}
            </h2>
          ))}
        </div>

        {hourRows(rules).map((hour) => (
          <div
            key={hour}
            className="grid gap-3 md:[grid-template-columns:var(--cols)]"
            style={columns}
          >
            <div className="flex items-center text-2xl font-semibold tabular-nums text-slate-700">
              {hour}:00
            </div>
            {bays.map((bay) => (
              <Cell
                key={bay.id}
                bayName={bay.name}
                date={date}
                cell={cells.get(`${bay.id}:${hour}`)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function Cell({
  bayName,
  date,
  cell,
}: {
  bayName: string
  date: string
  cell: ReturnType<typeof buildDayCells> extends Map<string, infer T> ? T | undefined : never
}) {
  if (!cell) return null

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium uppercase text-slate-700 md:hidden">{bayName}</p>

      {cell.bookings.map((booking) => (
        <BookingCard key={booking.id} booking={booking} hour={cell.hour} />
      ))}

      {cell.freeFrom && (
        <Link
          to="/admin/new"
          search={{ date, bay: cell.bayId, start: cell.freeFrom }}
          className="flex min-h-20 w-full items-center rounded-lg border-2 border-dashed border-slate-400 bg-white px-5 text-xl hover:bg-slate-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          {/* Printed, never revealed on hover -- an iPad has no hover. */}
          <span className="text-2xl">+</span>
          <span className="ml-3">
            Agregar {cell.freeIsWholeHour ? 'a las' : 'desde las'}{' '}
            <span className="font-semibold tabular-nums">{cell.freeFrom}</span>
          </span>
        </Link>
      )}
    </div>
  )
}

function BookingCard({ booking, hour }: { booking: CalendarBooking; hour: string }) {
  // A booking belongs to the row of its START hour. In any later hour it spans,
  // it shows as a carry-over line -- drawing it in full twice would read as two
  // separate lessons.
  const isCarryOver = booking.start.slice(0, 2) !== hour

  if (isCarryOver) {
    return (
      <div className="flex min-h-14 items-center rounded-lg bg-slate-200 px-5 text-lg text-slate-700">
        &uarr;&nbsp; {booking.customer_name ?? booking.admin_note ?? SIN_NOMBRE}, hasta{' '}
        <span className="ml-1 font-medium tabular-nums">{booking.end}</span>
      </div>
    )
  }

  return (
    <Link
      to="/admin/booking/$bookingId"
      params={{ bookingId: booking.id }}
      className={[
        'flex min-h-24 w-full flex-col justify-center rounded-lg border-2 px-5 py-3',
        'focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
        KIND_STYLES[booking.kind] ?? 'bg-white border-slate-300',
      ].join(' ')}
    >
      <span className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-sm font-semibold uppercase">
          {KIND_LABELS[booking.kind] ?? booking.kind}
        </span>
        <span className="text-lg tabular-nums text-slate-700">
          {booking.start} &rarr; {booking.end}
        </span>
      </span>
      <span className="text-xl font-medium">
        {booking.customer_name ?? booking.admin_note ?? SIN_NOMBRE}
      </span>
      {booking.coach_name && (
        <span className="text-base text-slate-700">con {booking.coach_name}</span>
      )}
      {booking.customer_phone && (
        <span className="text-base tabular-nums text-slate-700">{booking.customer_phone}</span>
      )}
    </Link>
  )
}
